// packages/shared/src/catalog/query.ts
//
// Catalog list query Zod schemas + bulk action payloads.
// Shared between all admin/merchant list endpoints (Wave 7 routes).
//
// Decisions implemented:
//   - Catalog filter / search params (search, categoryId, collectionId
//              status, sortBy, sortOrder, page/pageSize for admin offset pagination)
//   - Reorder pattern — ReorderPayloadSchema covers categories
//              collections, option values, add-ons (sortOrder must be int)
//   - (AC-27): Bulk pricing — BulkPricingUpdateBodySchema, reason
//              required per row for audit trail
//
// Pagination conventions (offset):
//   page 1-based, default 1; pageSize default 20, max 100.
// Cursor pagination (merchant):
//   cursor opaque token (base64, server-generated); limit default 20, max 100.
//
// ObjectIdSchema defined here (new) — no exported `ObjectIdSchema` found in
// Phase 1 shared code. Phase 1 wallet/types.ts uses private `ObjectIdHexSchema`
// (unexported). Canonical name `ObjectIdSchema` is introduced in this file.
// Post-wave deduplication: if a future wave centralises ObjectId into a
// shared primitives file, import from there and remove the declaration here.
import { z } from 'zod';
import { MoneyCentsSchema } from './primitives.js';
// ---------------------------------------------------------------------------
// ObjectIdSchema — MongoDB ObjectId hex string (24 lowercase hex chars)
//
// Reuse decision: Phase 1 has `ObjectIdHexSchema` in wallet/types.ts but it is
// NOT exported from the barrel. No canonical `ObjectIdSchema` export exists in
// packages/shared/src/. Declaring here as new.
// ---------------------------------------------------------------------------
/**
 * MongoDB ObjectId as a 24-character lowercase hexadecimal string.
 *
 * @example
 *   ObjectIdSchema.parse('507f1f77bcf86cd799439011')  // PASS
 *   ObjectIdSchema.parse('not-a-hex')                  // FAIL
 *   ObjectIdSchema.parse('507f1f77bcf86cd79943901')    // FAIL — 23 chars
 */
export const ObjectIdSchema = z.string().regex(/^[a-f0-9]{24}$/, 'invalid ObjectId — must be 24 lowercase hex characters');
// ---------------------------------------------------------------------------
// CatalogListQuery — admin offset-paginated list query params
//
// Flat query params pattern for all admin catalog list endpoints.
// page/pageSize use z.coerce.number() so URL string params ("?page=2")
// are automatically coerced to numbers.
// ---------------------------------------------------------------------------
/**
 * Query params for admin catalog list endpoints (offset pagination).
 *
 * Implements catalog filter/search params.
 *
 * @example
 *   CatalogListQuerySchema.parse({ sortBy: 'price', sortOrder: 'asc' })
 *   // PASS — page defaults to 1, pageSize defaults to 20
 *
 *   CatalogListQuerySchema.parse({
 *     search: 'shirt',
 *     categoryId: '507f1f77bcf86cd799439011',
 *     page: '3',
 *     pageSize: '50',
 *   })
 *   // PASS — page + pageSize coerced from strings (URL query param format)
 */
export const CatalogListQuerySchema = z.object({
    /** Free-text search term — trimmed, min 1 char when present */
    search: z.string().trim().min(1).max(200).optional(),
    /** Filter by category ObjectId */
    categoryId: ObjectIdSchema.optional(),
    /** Filter by collection ObjectId */
    collectionId: ObjectIdSchema.optional(),
    /**
     * Filter by fulfillment location ObjectId (2026-08-11).
     *
     * Matches against the blueprint's denormalized `locationIds[]` — the field
     * that exists for exactly this purpose (privacy: the merchant filters by
     * WHERE a product is made without ever seeing WHO makes it). It shipped
     * populated and exposed but with no filter wired to it, so "made in the US"
     * was expressible in the data and not in the API.
     */
    locationId: ObjectIdSchema.optional(),
    /**
     * Status filter — string for cross-resource flex.
     * Service layer validates against resource-specific allowed values.
     */
    status: z.string().optional(),
    /** Sort column name — service layer validates against allowlist */
    sortBy: z.string().optional(),
    /** Sort direction. Defaults to 'asc'. */
    sortOrder: z.enum(['asc', 'desc']).optional().default('asc'),
    /**
     * 1-based page number. z.coerce handles URL string → number.
     * Defaults to 1.
     */
    page: z.coerce.number().int().min(1).optional().default(1),
    /**
     * Items per page. z.coerce handles URL string → number.
     * Max 100 per CRUD-SURFACES.md pagination spec.
     * Defaults to 20.
     */
    pageSize: z.coerce.number().int().min(1).max(100).optional().default(20),
});
// ---------------------------------------------------------------------------
// CatalogCursorQuery — merchant cursor-paginated variant
//
// Merchant-facing catalog browse uses opaque cursor tokens for stable
// pagination across inserts/deletes (merchant side).
// ---------------------------------------------------------------------------
/**
 * Query params for merchant cursor-paginated catalog endpoints.
 *
 * @example
 *   CatalogCursorQuerySchema.parse({})
 *   // PASS — limit defaults to 20
 *
 *   CatalogCursorQuerySchema.parse({ cursor: 'eyJpZCI6IjUwN2YxZjc3In0=', limit: '50' })
 *   // PASS — cursor is opaque, limit coerced
 */
export const CatalogCursorQuerySchema = z.object({
    /** Free-text search term */
    search: z.string().optional(),
    /**
     * Opaque cursor token from previous page response (base64, server-generated).
     * Omit to start from the first page.
     */
    cursor: z.string().optional(),
    /**
     * Items per page. Max 100. Defaults to 20.
     * z.coerce handles URL string → number.
     */
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
// ---------------------------------------------------------------------------
// ReorderPayloadSchema — bulk reorder for categories, collections,
// option values, add-ons
//
// sortOrder must be a non-negative integer. Fractional values (e.g. 1.5)
// are rejected to prevent ordering drift.
// ---------------------------------------------------------------------------
/**
 * Single item in a reorder payload.
 */
export const ReorderItemSchema = z.object({
    /** ObjectId of the item to reorder */
    id: ObjectIdSchema,
    /**
     * New sort position — non-negative integer.
     * 1.5 is REJECTED (int required). Gaps are allowed (sparse ordering).
     */
    sortOrder: z.number().int('sortOrder must be an integer').min(0, 'sortOrder must be >= 0'),
});
/**
 * Bulk reorder payload.
 * Applies to: categories, collections, option values, add-ons.
 *
 * @example
 *   ReorderPayloadSchema.parse([{ id: '507f1f77bcf86cd799439011', sortOrder: 0 }])
 *   // PASS
 *
 *   ReorderPayloadSchema.parse([{ id: '507f1f77bcf86cd799439011', sortOrder: 1.5 }])
 *   // FAIL — int required
 *
 *   ReorderPayloadSchema.parse([])
 *   // FAIL — min 1 item required
 */
export const ReorderPayloadSchema = z.array(ReorderItemSchema).min(1, 'At least one item required in reorder payload');
// ---------------------------------------------------------------------------
// BulkPricingUpdateBodySchema — AC-27 bulk pricing import
//
// `reason` is REQUIRED per row for audit trail (AC-27 requirement).
// `priceCents` uses MoneyCentsSchema from primitives.ts (Task 1).
// Max 1000 rows per request to bound payload size.
// ---------------------------------------------------------------------------
/**
 * Single row in a bulk pricing update.
 * `reason` is REQUIRED for audit trail per AC-27.
 */
export const BulkPricingUpdateRowSchema = z.object({
    /** Blueprint variant ObjectId */
    variantId: ObjectIdSchema,
    /** Pricing tier ObjectId */
    tierId: ObjectIdSchema,
    /**
     * New price in integer cents (non-negative).
     * Uses MoneyCentsSchema from catalog/primitives.ts.
     */
    priceCents: MoneyCentsSchema,
    /**
     * Human-readable reason for this price change — required for audit log.
     * Must be 1–500 chars.
     */
    reason: z.string().min(1, 'reason is required').max(500, 'reason must be <= 500 characters'),
});
/**
 * Request body for bulk pricing update (AC-27).
 *
 * @example
 *   BulkPricingUpdateBodySchema.parse({
 *     updates: [{
 *       variantId: '507f1f77bcf86cd799439011',
 *       tierId: '507f1f77bcf86cd799439012',
 *       priceCents: 1099,
 *       reason: 'Q2 price increase',
 *     }],
 *     importSource: 'csv',
 *   })
 *   // PASS
 *
 *   // FAIL — reason missing:
 *   BulkPricingUpdateBodySchema.parse({
 *     updates: [{ variantId: '507f1f77bcf86cd799439011', tierId: '507f1f77bcf86cd799439012', priceCents: 999 }]
 *   })
 */
export const BulkPricingUpdateBodySchema = z.object({
    /**
     * Array of pricing update rows. Min 1, max 1000 per request.
     * Each row requires `reason` for audit trail.
     */
    updates: z.array(BulkPricingUpdateRowSchema)
        .min(1, 'At least one update row required')
        .max(1000, 'Maximum 1000 update rows per request'),
    /** Source of this bulk import — for audit/traceability */
    importSource: z.enum(['manual', 'csv', 'api']).optional(),
});
// ---------------------------------------------------------------------------
// BulkActionBodySchema — generic bulk action (set_status, change_collection,
// update_price)
//
// `payload` shape is action-dependent; service layer validates the specific
// payload fields after discriminating on `action`.
// ---------------------------------------------------------------------------
/**
 * Generic bulk action request body.
 * Used for admin bulk operations on catalog items.
 *
 * @example
 *   BulkActionBodySchema.parse({
 *     ids: ['507f1f77bcf86cd799439011'],
 *     action: 'set_status',
 *     payload: { status: 'active' },
 *   })
 *   // PASS
 *
 *   BulkActionBodySchema.parse({ ids: [], action: 'set_status' })
 *   // FAIL — ids must have at least 1 item
 */
export const BulkActionBodySchema = z.object({
    /**
     * ObjectIds of items to operate on.
     * Min 1, max 500 per request to bound DB query size.
     */
    ids: z.array(ObjectIdSchema)
        .min(1, 'At least one id required')
        .max(500, 'Maximum 500 ids per bulk action'),
    /** Discriminant for the type of bulk operation */
    action: z.enum(['set_status', 'change_collection', 'update_price']),
    /**
     * Action-specific payload — shape validated by service layer after
     * discriminating on `action`. Unknown keys accepted here intentionally.
     */
    payload: z.record(z.unknown()).optional(),
});
//# sourceMappingURL=query.js.map